// src/components/sections/CompanyValues.jsx
import React from 'react';
import { useTheme } from '../../context/ThemeContext';

const CompanyValues = () => {
  const { isDark } = useTheme();
  
  const values = [
    {
      title: 'Integrity',
      description: 'Operating professionally, openly, and ethically',
      icon: '🤝'
    },
    {
      title: 'Innovation',
      description: 'Continual improvement and technological advancement',
      icon: '💡'
    },
    {
      title: 'Client-Centricity',
      description: 'Prioritizing client needs and exceeding expectations',
      icon: '⭐'
    },
    {
      title: 'Collaboration',
      description: 'Working closely with clients and internal teams',
      icon: '🤝'
    },
    {
      title: 'Excellence',
      description: 'Maintaining high standards in projects and service delivery',
      icon: '🏆'
    }
  ];
  
  return (
    <section className={`relative py-24 sm:py-32 overflow-hidden ${isDark ? 'bg-black' : 'bg-white'}`}>
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/4 right-0 w-72 h-72 bg-blue-700/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-blue-700/20 rounded-full blur-3xl" />
      </div>
      
      {/* Grid pattern */}
      <div
        className={`absolute inset-0 ${isDark ? 'opacity-[0.03]' : 'opacity-[0.02]'}`}
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), 
                            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
          backgroundSize: "70px 70px",
        }}
      />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="mb-4">
            <span className={`text-xs sm:text-sm tracking-[0.2em] font-medium px-4 py-2 rounded-full inline-block ${
              isDark 
                ? 'text-blue-700 bg-blue-700/10' 
                : 'text-blue-700 bg-blue-100'
            }`}>
              OUR VALUES
            </span>
          </div>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            The principles that
            <br />
            <span className="bg-gradient-to-r from-blue-700 via-blue-700 to-blue-700 bg-clip-text text-transparent">
              guide us
            </span>
          </h2>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Built on a foundation of trust, innovation, and unwavering commitment to excellence
          </p>
        </div>
        
        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {values.map((value, index) => (
            <div 
              key={index} 
              className={`group backdrop-blur-sm rounded-2xl p-8 border transition-all duration-300 hover:transform hover:scale-[1.02] text-center ${
                isDark 
                  ? 'bg-white/5 border-white/10 hover:border-blue-700/30 hover:bg-white/10'
                  : 'bg-gray-100 border-gray-200 hover:border-blue-700 hover:bg-gray-50'
              }`}
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r from-blue-700 to-blue-700 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <span className="text-2xl">{value.icon}</span>
              </div>
              <h3 className={`text-xl font-semibold mb-3 transition-colors ${isDark ? 'text-white group-hover:text-blue-700' : 'text-gray-900 group-hover:text-blue-700'}`}>
                {value.title}
              </h3>
              <p className={`leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {value.description}
              </p>
            </div>
          ))}
        </div>
        
        {/* Why Choose DOROTS LTD */}
        <div className="mt-24">
          <div className={`backdrop-blur-sm rounded-3xl p-8 md:p-12 border ${
            isDark 
              ? 'bg-white/5 border-white/10' 
              : 'bg-gray-100 border-gray-200'
          }`}>
            <div className="text-center mb-8">
              <h3 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Why Choose <span className="bg-gradient-to-r from-blue-700 to-blue-700 bg-clip-text text-transparent">DOROTS LTD</span>
              </h3>
              <p className={`max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Partner with us for transformative business solutions
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center group">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                  isDark 
                    ? 'bg-blue-700/10 group-hover:bg-blue-700/20' 
                    : 'bg-blue-100 group-hover:bg-blue-200'
                }`}>
                  <span className={`text-2xl ${isDark ? 'text-blue-700' : 'text-blue-700'}`}>✓</span>
                </div>
                <h4 className={`text-lg font-semibold mb-2 transition-colors ${isDark ? 'text-white group-hover:text-blue-700' : 'text-gray-900 group-hover:text-blue-700'}`}>
                  Proven Expertise
                </h4>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Years of software development experience and successful projects
                </p>
              </div>
              
              <div className="text-center group">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                  isDark 
                    ? 'bg-blue-700/10 group-hover:bg-blue-700/20' 
                    : 'bg-blue-100 group-hover:bg-blue-200'
                }`}>
                  <span className={`text-2xl ${isDark ? 'text-blue-700' : 'text-blue-700'}`}>🎯</span>
                </div>
                <h4 className={`text-lg font-semibold mb-2 transition-colors ${isDark ? 'text-white group-hover:text-blue-700' : 'text-gray-900 group-hover:text-blue-700'}`}>
                  Customized Solutions
                </h4>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Products designed to fit specific business requirements
                </p>
              </div>
              
              <div className="text-center group">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                  isDark 
                    ? 'bg-blue-700/10 group-hover:bg-blue-700/20' 
                    : 'bg-blue-100 group-hover:bg-blue-200'
                }`}>
                  <span className={`text-2xl ${isDark ? 'text-blue-700' : 'text-blue-700'}`}>🔄</span>
                </div>
                <h4 className={`text-lg font-semibold mb-2 transition-colors ${isDark ? 'text-white group-hover:text-blue-700' : 'text-gray-900 group-hover:text-blue-700'}`}>
                  Comprehensive Support
                </h4>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  From consultation through post-implementation monitoring
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom decorative gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-700/30 to-transparent" />
    </section>
  );
};

export default CompanyValues;